'use client';

import { STORAGE_PARAMS } from '@utils/constants';

export type SandboxInfo = {
  id: string;
  callbackUrl: string;
};

export const saveSandboxInfo = (sandboxInfo: SandboxInfo): void => {
  sessionStorage.setItem(STORAGE_PARAMS.SANDBOX.INFO, JSON.stringify(sandboxInfo));
};

export const getSandboxInfo = (): SandboxInfo | null => {
  const data: string | null = sessionStorage.getItem(STORAGE_PARAMS.SANDBOX.INFO);

  if (data) {
    try {
      const info: SandboxInfo = JSON.parse(data);

      if (info.id) {
        return info;
      }
    } catch {
      return null;
    }
  }

  return null;
};

export const resetSandboxInfo = (): void => sessionStorage.removeItem(STORAGE_PARAMS.SANDBOX.INFO);

// Checks whether a given callback URL is valid. Parameters:
//   callbackUrl: Sandbox callback URL (string)
//   allowedCallbackUrlsRegexList: JSON string with array of Regex values used for validation (at least one needs to match)
// Examples:
//   isValidCallbackUrl('https://sandbox.io', '["^https?://.+$"]') == true
//   isValidCallbackUrl('https://sandbox.io', '["^https?://sandbox-test\.io$"]') == false
export const isValidCallbackUrl = (callbackUrl: string, allowedCallbackUrlsRegexList: string[]): boolean => {
  callbackUrl = callbackUrl.trim();

  if (callbackUrl && allowedCallbackUrlsRegexList && allowedCallbackUrlsRegexList.length > 0) {
    if (allowedCallbackUrlsRegexList.find((regexExpr: string) => callbackUrl.match(new RegExp(regexExpr)))) {
      return true;
    }
  }

  return false;
};

// Builds Sandbox URL. Parameters:
//   allowedCallbackUrlsRegexList: JSON string with array of Regex values used for validation (at least one needs to match)
//   defaultCallbackUrl: Sandbox callback URL (string)
export const buildSandboxUrl = (
  {
    sandboxInfo,
    cloudUrl,
    allowedCallbackUrlsRegexList,
    defaultCallbackUrl,
    userEmail,
  }:
  {
    sandboxInfo: SandboxInfo;
    cloudUrl: string;
    allowedCallbackUrlsRegexList: string[];
    defaultCallbackUrl: string;
    userEmail: string | undefined;
  },

): string => {
  if (!sandboxInfo.id) {
    return '';
  }

  let url: string = defaultCallbackUrl.trim();
  const callbackUrl: string = sandboxInfo.callbackUrl.trim();
  const isValid: boolean = isValidCallbackUrl(callbackUrl, allowedCallbackUrlsRegexList);

  if (callbackUrl && isValid) {
    url = callbackUrl;
  }

  if (!url) {
    // there's no default callback URL configured and no valid callback URL passed
    return '';
  }

  if (!url.endsWith('/')) {
    url += '/';
  }

  const cloudUrlEncoded: string = encodeURIComponent(cloudUrl);
  url += `course/${sandboxInfo.id}?cloudUrl=${cloudUrlEncoded}`;

  if (userEmail) {
    const userEmailEncoded: string = encodeURIComponent(userEmail);
    url += `&userEmail=${userEmailEncoded}`;
  }

  return url;
};
